import useSWR from 'swr'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { MyDatePicker, useDatePicker2 } from '@/components/MyDatePicker'
import WeekSelect from '@/components/week-select'
import RoasTable from './roas-data-table'
import RetTable from './retention-data-table'
import AdTable from './ad-data-table'
import Day0Chart from './roas-trend-chart'
import { useRoasStore } from '@/stores/roas'
import NotesDrawer from './notes-drawer'
import DailyNotesTable from './daily-notes-table'
import { useMemo, useState, useEffect } from 'react'
import Loader from '@/components/zthd/Loader'
import { getWeek } from '@/lib/date-utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { mutateRequest, fetcher } from '@/hooks'
import { troasQueryFromPicker } from '../troas-api-query'
import SummaryModeSelect from '@/components/summary-mode-select'
import { aggregateDailyData } from '../utils'
import RevenueMixView from './revenue-mix-view'
import RevenuePredictionView from './revenue-prediction-view'
import {
  mergePredictionDataByInstallDate,
  normalizeMergeInstallDateKey,
} from '../merge-prediction-install'

/**
 * 父组件（如 campaign-detail-page）已用同一 key 请求 /troas/:id 时传入，避免与子组件重复请求。
 */
const CampaignDetail = ({
  id,
  isTotal = false,
  compareIds = [],
  compareDates = [],
  mainDatesForCompare = [],
  onClearCompareDates,
  prefetchedTroas,
}) => {
  const { startDate, endDate, updateStartDate, updateEndDate } = useDatePicker2()
  const troasApiQuery = useMemo(
    () => troasQueryFromPicker(startDate, endDate),
    [startDate, endDate],
  )
  const [simplifiedMode, setSimplifiedMode] = useState(isTotal)
  const setDayOrder = useRoasStore((state) => state.setDayOrder)

  // 同 campaign 不同日期对比时，主区间同步为 Analyze 选定的目标时段
  useEffect(() => {
    if (mainDatesForCompare && mainDatesForCompare.length === 2 && updateStartDate && updateEndDate) {
      const start = String(mainDatesForCompare[0]).replace(/-/g, '')
      const end = String(mainDatesForCompare[1]).replace(/-/g, '')
      updateStartDate(start)
      updateEndDate(end)
    }
  }, [mainDatesForCompare, updateStartDate, updateEndDate])

  // 同 campaign 不同日期对比：对比区间的起止（YYYYMMDD），仅用于生成 roasDateCompare
  const [compareStart, compareEnd] = useMemo(() => {
    if (!compareDates || compareDates.length !== 2) return [null, null]
    const start = String(compareDates[0]).replace(/-/g, '')
    const end = String(compareDates[1]).replace(/-/g, '')
    return [start, end]
  }, [compareDates])

  useEffect(() => {
    setDayOrder('asc')
  }, [id, setDayOrder])

  useEffect(() => {
    setSimplifiedMode(isTotal)
  }, [isTotal])

  const {
    data: swrRoasData,
    isLoading: swrRoasLoading,
    isValidating: swrRoasValidating,
  } = useSWR(prefetchedTroas != null ? null : [`/troas/${id}`, troasApiQuery])

  const roasDataRaw =
    prefetchedTroas != null ? prefetchedTroas.data : swrRoasData
  const isLoading =
    prefetchedTroas != null ? prefetchedTroas.isLoading : swrRoasLoading
  const isValidating =
    prefetchedTroas != null ? prefetchedTroas.isValidating : swrRoasValidating

  const { data: compareResults, isValidating: isValidatingCompare, error: compareError } = useSWR(
    compareIds?.length > 0
      ? [
          'compare-all',
          [...compareIds].sort().join(','),
          JSON.stringify(troasApiQuery),
        ]
      : null,
    async ([, idsStr]) => {
      const ids = idsStr.split(',')
      const fetchOne = async (cid) => {
        const [roasRaw, camp, prediction, day90] = await Promise.all([
          fetcher([`/troas/${cid}`, troasApiQuery]),
          fetcher([`/campaigns/${cid}`]),
          fetcher([`/campaigns/${cid}/prediction`]),
          fetcher([`/campaigns/${cid}/roas/90`]),
        ])
        return { cid, roasRaw, camp, prediction, day90 }
      }
      return Promise.all(ids.map(fetchOne))
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )

  // 在数据从后端拉回来时，为每个日期分配固定的序号（按日期降序，最新日期序号为1）
  const roasData = useMemo(() => {
    if (!roasDataRaw?.dailyData) return roasDataRaw

    // 按日期降序排序，为每个日期分配固定的序号（最新日期序号为1）
    const sortedByDate = [...roasDataRaw.dailyData].sort((a, b) =>
      b.install_date.localeCompare(a.install_date),
    )

    // 为每个日期分配固定的序号，绑定到原始数据对象上
    sortedByDate.forEach((item, index) => {
      // 找到原始数据对象并绑定序号
      const originalItem = roasDataRaw.dailyData.find(
        (d) => d.install_date === item.install_date,
      )
      if (originalItem) {
        originalItem.index = index + 1
      }
    })

    return roasDataRaw
  }, [roasDataRaw])
  // Fetch ROAS line data
  const { data: roasLineData } = useSWR([`/troas/${id}/roasline`])
  // Fetch prediction data
  const { data: predictionData } = useSWR([`/campaigns/${id}/prediction`])

  const revisedRoas = useMemo(() => {
    if (!roasLineData?.data) return null
    return roasLineData.data
  }, [roasLineData])

  const { data: notesData } = useSWR([`/campaigns/${id}/notes`])

  // 主 Campaign 信息，用于对比时段命名（如 "{Campaign Name} (对比时段)"）
  const { data: mainCampaign } = useSWR(id ? [`/campaigns/${id}`] : null)

  // Fetch campaign total day 90 ROAS data
  const { data: campaignTotalDay90RoasData } = useSWR(
    id ? [`/campaigns/${id}/roas/90`] : null,
  )

  // 处理 day90 roas 数据，创建日期到 roas 值的映射
  const day90RoasMap = useMemo(() => {
    if (!campaignTotalDay90RoasData?.data) return new Map()

    const map = new Map()
    campaignTotalDay90RoasData.data.forEach((item) => {
      // 根据 date 字段匹配，将 roas 值存储到 Map 中
      if (item.date) {
        map.set(item.date, item.roas)
      }
    })
    return map
  }, [campaignTotalDay90RoasData])

  const mergedPredictionByInstallDate = useMemo(
    () => mergePredictionDataByInstallDate(predictionData),
    [predictionData],
  )

  const { summaryMode, weekFilter, dayMode } = useRoasStore()

  const handleRefrech = () => {
    mutateRequest(`/troas/${id}`)
    mutateRequest(`/campaigns/${id}/prediction`)
    mutateRequest(`/campaigns/${id}/notes`)
    mutateRequest(`/troas/${id}/roasline`)
    mutateRequest(`/campaigns/${id}/roas/90`)
    if (compareIds?.length > 0) {
      compareIds.forEach(cid => {
        mutateRequest(`/troas/${cid}`)
        mutateRequest(`/campaigns/${cid}/prediction`)
        mutateRequest(`/campaigns/${cid}/roas/90`)
      })
      mutateRequest(['compare-all', compareIds])
    }
  }

  const roas = useMemo(() => {
    const redueDailyData = () => {
      const dailyData = roasData?.dailyData ?? []
      const filteredDailyData = dailyData.filter((item) => {
        const itemDate = item.install_date.split('-').join('')
        return itemDate >= startDate && itemDate <= endDate
      })
      if (weekFilter === null) return filteredDailyData

      return dailyData.filter((item) => {
        const [dayIndex] = getWeek(item.install_date)
        return dayIndex === Number(weekFilter)
      })
    }
    const dailyData = redueDailyData().map((item) => {
      const mergedPrediction =
        mergedPredictionByInstallDate.get(
          normalizeMergeInstallDateKey(item.install_date),
        ) || {}
      const predictRevenueDays = mergedPrediction.predictRevenueDays || {}

      // 获取 day90 的 roas 值
      const day90Roas = day90RoasMap.get(item.install_date)
      // 检查 days 中是否存在 day90 数据
      const hasDay90Data = item.days?.day90 !== undefined

      // 返回包含 index 字段的新对象，index 已经在数据拉回来时固定绑定
      return {
        ...item,
        index: item.index, // 使用已经固定的序号，不重新分配
        days: item.days, // 保持 days 对象不变
        ...(day90Roas !== undefined && hasDay90Data && { total_d90_roas: day90Roas * 100 }), // roas 值转换为百分比格式（乘以100），作为独立字段，只有当 days 中存在 day90 数据时才展示
        predictRevenueDays,
        predictRevenueAccDays: mergedPrediction.predictRevenueAccDays || {},
        cumulativePredictRevenueDays:
          mergedPrediction.predictRevenueAccDays || {},
        predictRoasAccDays: mergedPrediction.predictRoasAccDays || {},
        // Store all models data for chart usage
        allModelsData: mergedPrediction.allModelsData || new Map(),
      }
    })

    const aggregatedDailyData = aggregateDailyData(dailyData, summaryMode)

    return { ...roasData, dailyData: aggregatedDailyData, date: { from: startDate, to: endDate } }
  }, [
    roasData,
    weekFilter,
    startDate,
    endDate,
    mergedPredictionByInstallDate,
    day90RoasMap,
    summaryMode,
  ])

  // 其他 campaign 对比：主时段一条；若已选时间对比则再生成「该 campaign 在对比时段」一条，与 allCompareRoas 一起展示
  const compareRoas = useMemo(() => {
    if (compareIds.length === 0) return null
    if (!compareResults || compareResults.length === 0) return null

    const buildEntry = (result, dateFrom, dateTo, isDateCompare = false, nameSuffix = '') => {
      const { roasRaw, camp, prediction, day90, cid } = result

      const processedRoasData = (() => {
        if (!roasRaw?.dailyData) return roasRaw
        const sortedByDate = [...roasRaw.dailyData].sort((a, b) =>
          b.install_date.localeCompare(a.install_date),
        )
        sortedByDate.forEach((item, index) => {
          const originalItem = roasRaw.dailyData.find(
            (d) => d.install_date === item.install_date,
          )
          if (originalItem) {
            originalItem.index = index + 1
          }
        })
        return roasRaw
      })()

      const day90Map = new Map()
      if (day90?.data) {
        day90.data.forEach((item) => {
          if (item.date) day90Map.set(item.date, item.roas)
        })
      }

      const mergedPredictionsMap = mergePredictionDataByInstallDate(prediction)

      const rawDaily = processedRoasData?.dailyData ?? []
      let dailyDataSrc
      if (weekFilter === null) {
        dailyDataSrc = rawDaily.filter((item) => {
          const itemDate = item.install_date.split('-').join('')
          return itemDate >= dateFrom && itemDate <= dateTo
        })
      } else {
        dailyDataSrc = rawDaily.filter((item) => {
          const [dayIndex] = getWeek(item.install_date)
          return dayIndex === Number(weekFilter)
        })
      }

      const dailyData = dailyDataSrc.map((item) => {
        const mergedPrediction =
          mergedPredictionsMap.get(
            normalizeMergeInstallDateKey(item.install_date),
          ) || {}
        const predictRevenueDays = mergedPrediction.predictRevenueDays || {}
        const d90Roas = day90Map.get(item.install_date)
        const hasDay90Data = item.days?.day90 !== undefined
        return {
          ...item,
          index: item.index,
          days: item.days,
          ...(d90Roas !== undefined && hasDay90Data && { total_d90_roas: d90Roas * 100 }),
          predictRevenueDays,
          predictRevenueAccDays: mergedPrediction.predictRevenueAccDays || {},
          cumulativePredictRevenueDays:
            mergedPrediction.predictRevenueAccDays || {},
          predictRoasAccDays: mergedPrediction.predictRoasAccDays || {},
          allModelsData: mergedPrediction.allModelsData || new Map(),
        }
      })

      const aggregatedCompareDailyData = aggregateDailyData(dailyData, summaryMode)
      const baseName = camp?.name || cid || 'Compare'
      const campaignName = nameSuffix ? `${baseName} (${nameSuffix})` : baseName

      return {
        ...processedRoasData,
        dailyData: aggregatedCompareDailyData,
        date: { from: dateFrom, to: dateTo },
        campaignName,
        ...(isDateCompare && { isDateCompare: true }),
      }
    }

    const entries = []
    compareResults
      .filter((result) => compareIds.includes(result.cid))
      .forEach((result) => {
        entries.push(buildEntry(result, startDate, endDate, false, ''))
        if (compareStart && compareEnd) {
          entries.push(buildEntry(result, compareStart, compareEnd, true, '对比时段'))
        }
      })
    return entries
  }, [
    compareResults,
    compareIds,
    weekFilter,
    startDate,
    endDate,
    compareStart,
    compareEnd,
    summaryMode,
  ])

  // 同 campaign 不同日期对比：用当前 campaign 在对比日期区间内的数据生成一条“对比时段”数据，与 compareRoas 同结构
  const roasDateCompare = useMemo(() => {
    if (!compareStart || !compareEnd || !roasData?.dailyData) return null
    const redueDailyData = () => {
      const dailyData = roasData.dailyData.filter((item) => {
        const itemDate = item.install_date.split('-').join('')
        return itemDate >= compareStart && itemDate <= compareEnd
      })
      if (weekFilter === null) return dailyData
      return dailyData.filter((item) => {
        const [dayIndex] = getWeek(item.install_date)
        return dayIndex === Number(weekFilter)
      })
    }
    const dailyData = redueDailyData().map((item) => {
      const mergedPrediction =
        mergedPredictionByInstallDate.get(
          normalizeMergeInstallDateKey(item.install_date),
        ) || {}
      const predictRevenueDays = mergedPrediction.predictRevenueDays || {}
      const day90Roas = day90RoasMap.get(item.install_date)
      const hasDay90Data = item.days?.day90 !== undefined
      return {
        ...item,
        index: item.index,
        days: item.days,
        ...(day90Roas !== undefined && hasDay90Data && { total_d90_roas: day90Roas * 100 }),
        predictRevenueDays,
        predictRevenueAccDays: mergedPrediction.predictRevenueAccDays || {},
        cumulativePredictRevenueDays:
          mergedPrediction.predictRevenueAccDays || {},
        predictRoasAccDays: mergedPrediction.predictRoasAccDays || {},
        allModelsData: mergedPrediction.allModelsData || new Map(),
      }
    })
    const aggregatedDailyData = aggregateDailyData(dailyData, summaryMode)
    const campaignLabel = mainCampaign?.name ? `${mainCampaign.name} (对比时段)` : 'Period B (对比时段)'
    return {
      ...roasData,
      dailyData: aggregatedDailyData,
      date: { from: compareStart, to: compareEnd },
      campaignName: campaignLabel,
      isDateCompare: true, // 表格/图表按行索引对齐，不按 install_date 匹配
    }
  }, [
    compareStart,
    compareEnd,
    roasData,
    weekFilter,
    summaryMode,
    mergedPredictionByInstallDate,
    day90RoasMap,
    mainCampaign?.name,
  ])

  // 合并对比数据，顺序：主 | 对比 campaign 同时段 | 同 campaign 对比时段 | 对比 campaign 对比时段
  const allCompareRoas = useMemo(() => {
    const byCampaign = Array.isArray(compareRoas) ? compareRoas : compareRoas ? [compareRoas] : []
    const mainPeriodCompares = byCampaign.filter((c) => !c.isDateCompare)
    const comparePeriodCompares = byCampaign.filter((c) => c.isDateCompare)
    const byDate = roasDateCompare ? [roasDateCompare] : []
    return [...mainPeriodCompares, ...byDate, ...comparePeriodCompares]
  }, [compareRoas, roasDateCompare])

  const notes = useMemo(
    () =>
      notesData?.map((n) => ({
        ...n,
        cid: id,
      })) ?? [],
    [notesData, id],
  )

  const isInitialLoading = isLoading && !roasDataRaw

  if (isInitialLoading) return <Loader>Loading...</Loader>

  return (
    <div className="space-y-4 py-4">
      <Tabs defaultValue="roas" className="w-full">
        <div className="flex items-center justify-between gap-2 sm:flex-wrap">
          <TabsList
            className={`grid ${
              isTotal ? 'w-[700px] grid-cols-7' : 'w-[800px] grid-cols-8'
            }  gap-2`}
          >
            <TabsTrigger value="roas">Roas</TabsTrigger>
            <TabsTrigger value="prediction">Prediction</TabsTrigger>
            <TabsTrigger value="day0">Day-Chart</TabsTrigger>
            <TabsTrigger value="mix">MIX</TabsTrigger>
            <TabsTrigger value="budget">Budget</TabsTrigger>
            <TabsTrigger value="ret">Ret</TabsTrigger>
            <TabsTrigger value="ad">AD</TabsTrigger>
            {!isTotal && <TabsTrigger value="notes">Notes</TabsTrigger>}
          </TabsList>
          <Button onClick={handleRefrech} variant="outline">
            refresh
          </Button>
          <div className="flex flex-wrap items-center gap-4">
            {/* 列表展示所有日期（含非完整日期），不按完整日期截止过滤；用户仍可通过「不完整数据」开关限制范围 */}
            <MyDatePicker
              datePickerStore={useDatePicker2}
              getMaxDate="roas-complete"
              showMaxDate={true}
              defaultAllowIncomplete={!isTotal}
            />
            {compareStart && compareEnd && onClearCompareDates && (
              <div className="inline-flex items-center gap-2 rounded-md border border-input bg-muted/50 px-2 py-1 text-xs">
                <span className="text-muted-foreground">
                  Compare period: {compareStart.slice(0, 4)}-{compareStart.slice(4, 6)}-{compareStart.slice(6, 8)} ~ {compareEnd.slice(0, 4)}-{compareEnd.slice(4, 6)}-{compareEnd.slice(6, 8)}
                </span>
                <Button type="button" variant="ghost" size="sm" className="h-6 px-1 text-muted-foreground hover:text-foreground" onClick={onClearCompareDates}>
                  Clear
                </Button>
              </div>
            )}
            <WeekSelecter />
            <SummaryModeSelect />
            <div className="flex items-center space-x-2">
              <Checkbox
                id="simplified-mode"
                checked={simplifiedMode}
                onCheckedChange={setSimplifiedMode}
              />
              <Label htmlFor="simplified-mode">精简模式</Label>
            </div>
          </div>
        </div>
        <TabsContent value="roas" className="w-full">
          <RoasTable
            roas={roas}
            compareRoas={allCompareRoas}
            revisedRoas={revisedRoas}
            day0RoasLine={roasData?.day0RoasLine}
            day0RoasLineTotalFallback={roasData?.day0RoasLineTotalFallback}
            isTotal={isTotal}
            simplifiedMode={simplifiedMode}
            campaignId={id}
            summaryMode={summaryMode}
          />
        </TabsContent>
        <TabsContent value="prediction" className="w-full">
          {predictionData?.champion?.source === 'multi_horizon' &&
            predictionData.champion.horizons?.length === 0 && (
              <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
                No recommended model available for any horizon.
              </div>
            )}
          {predictionData?.champion?.predictionLevel === 'unsupported-organic' && (
            <div className="flex items-center justify-center h-10 text-muted-foreground text-sm bg-muted/40 rounded mb-2">
              Organic campaigns are excluded from official prediction models.
            </div>
          )}
          {predictionData?.champion?.predictionLevel === 'aggregate-media-group' && (
            <div className="flex items-center justify-center h-10 text-muted-foreground text-sm bg-muted/40 rounded mb-2">
              Media group predictions are pending rollup — no champion model available yet.
            </div>
          )}
          <RoasTable
            roas={roas}
            compareRoas={allCompareRoas}
            revisedRoas={revisedRoas}
            day0RoasLine={roasData?.day0RoasLine}
            day0RoasLineTotalFallback={roasData?.day0RoasLineTotalFallback}
            isTotal={isTotal}
            simplifiedMode={simplifiedMode}
            campaignId={id}
            targetDays={[0, 3, 7, 14, 30, 45, 60, 90, 120, 150]}
            isPrediction={true}
            summaryMode={summaryMode}
          />
        </TabsContent>
        <TabsContent value="day0" className="w-full">
          <Day0Chart 
            roas={roas} 
            compareRoas={allCompareRoas} 
            dayMode={dayMode} 
            day0RoasLine={roasData?.day0RoasLine}
            day0RoasLineTotalFallback={roasData?.day0RoasLineTotalFallback}
          />
        </TabsContent>
        <TabsContent value="ret" className="w-full">
          <RetTable
            roas={roas}
            compareRoas={allCompareRoas}
            isTotal={isTotal}
            simplifiedMode={simplifiedMode}
            summaryMode={summaryMode}
          />
          {/* <DailyRetTable roas={roas} isTotal={isTotal} /> */}
        </TabsContent>
        <TabsContent value="ad" className="w-full">
          <AdTable
            roas={roas}
            compareRoas={allCompareRoas}
            isTotal={isTotal}
            simplifiedMode={simplifiedMode}
            summaryMode={summaryMode}
          />
        </TabsContent>
        <TabsContent value="mix" className="w-full">
          <RevenueMixView roasData={roasData} isSimplified={simplifiedMode} summaryMode={summaryMode} />
        </TabsContent>
        <TabsContent value="budget" className="w-full">
          <RevenuePredictionView roasData={roasData} />
        </TabsContent>

        {!isTotal && (
          <TabsContent value="notes" className="w-full">
            <DailyNotesTable notes={notes} />
          </TabsContent>
        )}
      </Tabs>
      <NotesDrawer notes={notes} cid={id} />
    </div>
  )
}
const WeekSelecter = () => {
  const weekFilter = useRoasStore((state) => state.weekFilter)
  const setWeekFilter = useRoasStore((state) => state.setWeekFilter)
  const summaryMode = useRoasStore((state) => state.summaryMode)

  if (summaryMode !== 'daily') return null

  return <WeekSelect value={weekFilter} onChange={setWeekFilter} />
}

export default CampaignDetail
